import { api } from "@/lib/api";

/**
 * Press & media coverage.
 *
 * The odd one out in the Resource library: unlike Glossary, FAQ, News and
 * Mastermind there is no admin screen and no resource_contents row behind this.
 * The list is read live from the public marketing site
 * (https://www.businesslocating.com/press) by the API and cached there, so the
 * portal can never show coverage the website doesn't.
 *
 * Mirrors app/schemas/press.py — keep the two in sync.
 */

export interface PressItem {
  /** The only two fields the API guarantees; everything else is best-effort. */
  title: string;
  url: string;

  /** Publication that ran the story, e.g. "USA TODAY". */
  outlet: string | null;
  outlet_logo_url: string | null;
  summary: string | null;

  /** ISO date (YYYY-MM-DD) from the source page's <time> element. */
  published_on: string | null;
  /** The date exactly as the press page printed it — preferred when present. */
  published_label: string | null;

  /** The "Featured" pill the press page puts on its highlighted items. */
  featured: boolean;
}

export interface PressFeed {
  items: PressItem[];
  /** The press page itself — the page links out to it as the canonical list. */
  source_url: string;
  fetched_at: string;
  /** True when the marketing site was unreachable and this is the last good copy. */
  stale: boolean;
}

export const fetchPressFeed = () => api.get<PressFeed>("/resources/press");

/**
 * The date to show for an item.
 *
 * Prefers the label the press page itself rendered, so the portal reads exactly
 * like the website, and formats the ISO date only as a fallback. Returns null
 * rather than a placeholder — the card drops the date row entirely when the
 * source carried none.
 */
export function formatPressDate(item: PressItem): string | null {
  if (item.published_label) return item.published_label;
  if (!item.published_on) return null;
  // Parse as UTC noon: a bare "YYYY-MM-DD" is treated as UTC midnight, which
  // renders as the previous day for anyone west of Greenwich.
  const parsed = new Date(`${item.published_on}T12:00:00Z`);
  return Number.isNaN(parsed.getTime())
    ? null
    : parsed.toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });
}
